// Transport problem solver hook

import { useState, useCallback } from 'react';
import { solveTransport } from '../../../../Services/PathServices/ApiService';
import { processTransportSolution, validateProcessedData } from '../../../../Services/PathServices/TransportDataProcessor';
import { validateQuantities } from '../utils/quantityValidator';

export const useTransportSolver = (locations, setPath, saveAutoRoutes = null) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [solution, setSolution] = useState(null);
  const [isolationInfo, setIsolationInfo] = useState(null);

  const solveProblem = useCallback(async (method) => {
    if (locations.length < 2) {
      setError('At least 2 location points are required');
      return;
    }
    
    const supplies = locations.filter(loc => (loc.tpType || '').toLowerCase() === 'supply');
    const demands = locations.filter(loc => (loc.tpType || '').toLowerCase() === 'demand');
    
    if (supplies.length === 0 || demands.length === 0) {
      setError('Need at least one Supply and one Demand location');
      return;
    }
    
    // Validate quantities
    const quantityErrors = validateQuantities(locations);
    if (quantityErrors.length > 0) {
      setError(`Quantity validation failed:\n${quantityErrors.join('\n')}`);
      return;
    }
    
    setLoading(true);
    setError(null);
    setSolution(null);
    
    try {
      const supplyLocations = supplies.map((loc, index) => ({
        latitude: loc.latitude,
        longitude: loc.longitude,
        name: String(loc.number || loc.name || `Supply_${index + 1}`)
      }));
      
      const demandLocations = demands.map((loc, index) => ({
        latitude: loc.latitude,
        longitude: loc.longitude,
        name: String(loc.number || loc.name || `Demand_${index + 1}`)
      }));
      
      const supplyAmounts = supplies.map(loc => Number(loc.quantity));
      const demandAmounts = demands.map(loc => Number(loc.quantity));
      
      const response = await solveTransport(
        supplyLocations,
        demandLocations,
        supplyAmounts,
        demandAmounts,
        method,
        null,
        true
      );
      
      // Process the solution data
      const processedData = processTransportSolution(response, supplies, demands, locations);
      
      // Validate processing results
      const validation = validateProcessedData(processedData);
      if (!validation.isValid) {
        console.error('Data processing validation failed:', validation.errors);
        setError(`Data processing failed: ${validation.errors.join(', ')}`);
        return;
      }
      
      // Set results
      setSolution(processedData.sidebarData);
      setIsolationInfo(processedData.isolationInfo);
      
      // Save auto routes to memory for persistence
      if (saveAutoRoutes) {
        const autoRouteData = {
          solution: processedData.sidebarData,
          isolationInfo: processedData.isolationInfo,
          mapData: processedData.mapData,
          method: method,
          timestamp: new Date().toISOString(),
          locations: locations
        };
        saveAutoRoutes(autoRouteData);
      }
      
      // Set map data directly to autoPath
      if (processedData.mapData && processedData.mapData.features.length > 0) {
        setPath(processedData.mapData, 'Auto Path', true);
      } else {
        setPath([], 'Auto Path', true);
      }
      
    } catch (err) {
      setError(`Solving failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [locations, setPath]);

  return {
    loading,
    error,
    solution,
    isolationInfo,
    solveProblem
  };
};
